import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// DigestStatusPanelRow.tsx -- one row inside the unified Digest
// Status panel (DigestStatusButton's dropdown / detached window).
//
// Each row represents one document's digest job. Displays:
//   - filename + overall status pill + total elapsed
//   - close button (`x`) -- removes from panel (does NOT cancel)
//   - chevron to expand stage detail inline
//
// When expanded: shows the same stage tracker the floating window
// uses (extract / chunk / embed pills + (i) info button per stage).
//
// Polls the BFF status endpoint every 30s while not terminal.
// Reports its current overall status to the parent via onSummary so
// the top-bar button can color-code by aggregate state.
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../utils/api.js';
import { useDocumentStatusStore, usePanelStatusEntry, } from '../../state/documentStatusStore.js';
import { useUploadProgress } from '../../state/uploadProgressStore.js';
// Single polling cadence for in-flight digest jobs. 15s is the
// sweet spot between "user sees state changes promptly" and "we
// don't hammer the BFF/raindb-api". Cheap on the server because
// every level of the read path is cached: the BFF's status route
// hits raindb-api which serves relay-tree reads from the objects
// cache (raindb-phoenix-oc/pkg/objects/cache.go), so a poll storm
// across many tabs collapses to a single S3 round-trip.
//
// Polling stops when overall status is terminal (complete/failed),
// so a finished job costs nothing.
const POLL_INTERVAL_MS = 15_000;
export function DigestStatusPanelRow({ projectId, documentId, onClose, onSummary, }) {
    const entry = usePanelStatusEntry(documentId);
    const setExpandedInPanel = useDocumentStatusStore((s) => s.setExpandedInPanel);
    // Upload phase is purely client-side -- the BFF can't see CDU
    // progress (the bytes go browser->S3 direct). When this row's
    // documentId has an upload-progress entry, we synthesise an
    // "upload" stage from it that prepends to the server-driven
    // digest stages. Once the upload completes the entry stays
    // 'complete' and the digest pipeline takes over for the rest of
    // the timeline.
    const uploadProgress = useUploadProgress(documentId);
    const [status, setStatus] = useState(null);
    const [err, setErr] = useState(null);
    const lastFetchAt = useRef(0);
    // Effective overall: when an upload is in flight or failed, that
    // dominates the row's status. Once upload completes the server-side
    // overall takes over (digest phase). When server status is
    // 'no-action-found' but upload is complete, treat as 'queued' (the
    // digest action just hasn't been scheduled yet -- matches the
    // disabledFlows=['digest'] aidocs case where digest is intentionally
    // suppressed; user moves doc out of aidocs to fire it).
    const serverOverall = (status?.overall ?? 'unknown');
    const overall = (() => {
        if (uploadProgress) {
            if (uploadProgress.status === 'failed')
                return 'failed';
            if (uploadProgress.status === 'pending')
                return 'queued';
            if (uploadProgress.status === 'uploading')
                return 'streaming';
            // upload complete -- fall through to server status. If server
            // hasn't seen the action yet, we're between phases; treat as
            // 'running' so the button stays green.
            if (serverOverall === 'unknown' || (status?.overall === 'no-action-found')) {
                return 'running';
            }
        }
        return serverOverall;
    })();
    const isTerminal = overall === 'complete' || overall === 'failed';
    const refresh = useCallback(async () => {
        setErr(null);
        try {
            const r = await api.get(`/p/${projectId}/documents/${documentId}/status`);
            setStatus(r.status);
            lastFetchAt.current = Date.now();
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : 'failed to load status');
        }
    }, [projectId, documentId]);
    useEffect(() => {
        void refresh();
    }, [refresh]);
    // Fire an immediate refresh the instant the upload PUT completes
    // so we don't wait up to POLL_INTERVAL_MS for the digest stages
    // to first appear. The PUT-complete -> S3 event -> cyclone CDU
    // completion -> first relay write race usually settles in 2-5s,
    // well within the 15s poll, but the immediate kick eliminates
    // the worst-case "user stares at complete Upload row with no
    // digest detail" window.
    const uploadCompletedAt = uploadProgress?.completedAt;
    useEffect(() => {
        if (isTerminal)
            return;
        if (!uploadCompletedAt)
            return;
        void refresh();
    }, [isTerminal, uploadCompletedAt, refresh]);
    // Steady-state poll for any in-flight job. Stops when terminal.
    useEffect(() => {
        if (isTerminal)
            return;
        const id = setInterval(() => { void refresh(); }, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [isTerminal, refresh]);
    // Surface this row's status up to the button for color rollup.
    useEffect(() => {
        onSummary({ documentId, status: overall });
    }, [documentId, overall, onSummary]);
    const filename = status?.filename ?? entry?.filename ?? uploadProgress?.filename ?? '(loading)';
    const expanded = !!entry?.expandedInPanel;
    const totalElapsed = durationStr(uploadProgress?.startedAt ?? status?.startedAt, status?.completedAt);
    // Build the visible stage list. Three layers of synthesis:
    //
    //   1. Upload stage (CDU only) -- prepended from
    //      uploadProgressStore. Server can't see CDU upload progress
    //      because bytes go browser->S3 direct, so this stage exists
    //      ONLY in the client.
    //
    //   2. Server-driven digest stages -- from the /status endpoint.
    //      Real timings + real outputs (extract page count, chunk
    //      count, embed token cost). These replace the placeholders
    //      below as soon as the server starts reporting them.
    //
    //   3. Placeholder digest stages -- synthesised when:
    //        - upload phase exists AND
    //        - upload phase has completed AND
    //        - server hasn't yet returned any stages
    //      Shown as 'queued' so the user sees the pipeline they're
    //      waiting for ('extract / chunk / embed' visibly enumerated)
    //      instead of a "complete upload, then nothing" panel that
    //      could be misread as "stuck" or "done."
    //
    //      Mirrors the formation's digest action pipeline shape:
    //      `digest` formation declares stages
    //      [digest-extract, digest-chunk, digest-enrich-block,
    //       digest-embed]; we surface a 3-stage user-facing view
    //      (extract / chunk / embed) collapsing enrich-block as an
    //      implementation detail.
    //
    //      Once the server returns ANY stage, that takes precedence
    //      and placeholders are dropped (the server is authoritative
    //      for what's actually happening; placeholders are only
    //      anti-stale UI).
    //
    // For inline writes there's no upload stage; the digest stages
    // are written directly by the server-side flow and arrive within
    // ~100-500ms of the WriteDroplet call, so placeholders aren't
    // needed (the gap is too brief to be visible).
    const stages = [];
    if (uploadProgress) {
        const fraction = uploadProgress.totalBytes > 0
            ? uploadProgress.bytesSent / uploadProgress.totalBytes
            : 0;
        const uploadStageStatus = uploadProgress.status === 'complete' ? 'complete'
            : uploadProgress.status === 'failed' ? 'failed'
                : uploadProgress.status === 'uploading' ? 'streaming'
                    : 'queued';
        stages.push({
            name: 'upload',
            status: uploadStageStatus,
            startedAt: uploadProgress.startedAt,
            ...(uploadProgress.completedAt ? { completedAt: uploadProgress.completedAt } : {}),
            progress: fraction,
            progressLabel: `${formatBytes(uploadProgress.bytesSent)} / ${formatBytes(uploadProgress.totalBytes)}`,
            ...(uploadProgress.error ? { output: { error: uploadProgress.error } } : {}),
        });
    }
    const haveServerStages = !!(status?.stages && status.stages.length > 0);
    if (haveServerStages) {
        for (const s of status.stages)
            stages.push(s);
    }
    else if (uploadProgress?.status === 'complete'
        && !uploadProgress.error
        && uploadProgress.pendingFlows
        && uploadProgress.pendingFlows.length > 0) {
        // Placeholder pipeline stages -- the upcoming work the user is
        // waiting for. Server-driven (NOT a hardcoded list) -- captured
        // from the /reserve response's postUploadFlows[].stages[] field
        // and stashed in the upload-progress store at upload-start time.
        //
        // Stage names match what /status will eventually return in
        // stages[].name, so when the server-driven stages arrive the
        // row's visual identity stays stable as the placeholders get
        // replaced via PanelStageView's StageRow keying.
        //
        // Disabled flows (e.g. aidocs uploads with disabledFlows:
        // ['digest']) intentionally produce ZERO placeholder stages
        // here -- the panel shows just the upload phase + a 'complete'
        // overall, which is honest about "no follow-up work scheduled".
        //
        // PROJECT_REQUIREMENTS section 1.2 #5 (default to simpler) +
        // section 5.3 (deterministic-or-fail): server is the source of
        // truth; client doesn't fabricate. Output is empty per the
        // rule; status='queued' is honest about the placeholder state.
        for (const flow of uploadProgress.pendingFlows) {
            if (!flow.enabled)
                continue;
            for (const stage of flow.stages) {
                stages.push({
                    name: stage.name,
                    displayName: stage.displayName,
                    status: 'queued',
                });
            }
        }
    }
    return (_jsxs("div", { className: `digest-row digest-row-${overall}`, children: [_jsxs("button", { type: "button", className: "digest-row-head", onClick: () => setExpandedInPanel(documentId, !expanded), "aria-expanded": expanded, children: [_jsx("span", { className: "digest-row-chevron", "aria-hidden": true, children: expanded ? '\u25BE' : '\u25B8' }), _jsx("span", { className: `digest-row-overall digest-row-overall-${overall}`, children: overallGlyph(overall) }), _jsx("span", { className: "digest-row-name", title: filename, children: filename }), totalElapsed && (_jsx("span", { className: "digest-row-elapsed", children: totalElapsed }))] }), _jsx("button", { type: "button", className: "digest-row-close", onClick: onClose, title: "Remove from this list (digest keeps running)", "aria-label": "Remove from list", children: '\u2715' }), expanded && (_jsxs("div", { className: "digest-row-stages", children: [err && _jsx("div", { className: "doc-status-error", children: err }), !status && !err && stages.length === 0 && (_jsx("div", { className: "doc-status-loading", children: 'Loading\u2026' })), stages.length === 0 && status && !err && (_jsx("div", { className: "doc-status-stage-empty", children: overall === 'queued' || overall === 'unknown'
                            ? 'Digest hasn\u2019t started yet.'
                            : 'No stages reported.' })), stages.map((stage, i) => (_jsx(PanelStageView, { stage: stage }, `${stage.name}-${i}`)))] }))] }));
}
function PanelStageView({ stage }) {
    const [showJson, setShowJson] = useState(false);
    const stageStatus = stage.status ?? 'queued';
    const dur = stage.startedAt
        ? durationStr(stage.startedAt, stage.completedAt)
        : '';
    const durLabel = stageStatus === 'streaming' && dur ? `running for ${dur}` : dur;
    const hasOutput = !!stage.output
        && typeof stage.output === 'object'
        && Object.keys(stage.output).length > 0;
    // Progress is set only on the synthesised upload stage. Server
    // stages don't populate it (their durations + status pills carry
    // enough signal). When set, render a sub-bar between the head
    // and any output rows.
    const showProgress = typeof stage.progress === 'number'
        && stage.progress >= 0
        && stage.progress <= 1
        && (stageStatus === 'streaming' || stageStatus === 'queued');
    const progressPct = showProgress ? Math.round(stage.progress * 100) : 0;
    return (_jsxs("div", { className: `doc-status-stage doc-status-stage-${stageStatus}`, children: [_jsxs("div", { className: "doc-status-stage-head", children: [_jsxs("span", { className: `doc-status-stage-pill doc-status-stage-pill-${stageStatus}`, children: [stageGlyph(stageStatus), " ", stageStatus] }), _jsx("span", { className: "doc-status-stage-name", children: stage.displayName ?? stage.name }), durLabel && _jsx("span", { className: "doc-status-stage-duration", children: durLabel }), hasOutput && (_jsx("button", { type: "button", className: "doc-status-stage-info", onClick: () => setShowJson((v) => !v), title: showJson ? 'Hide stage output' : 'Show stage output', "aria-expanded": showJson, children: '\u24D8' }))] }), showProgress && (_jsxs("div", { className: "doc-status-stage-progress", children: [_jsx("div", { className: "doc-status-stage-progress-track", children: _jsx("div", { className: "doc-status-stage-progress-fill", style: { width: `${progressPct}%` }, role: "progressbar", "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": progressPct }) }), stage.progressLabel && (_jsxs("span", { className: "doc-status-stage-progress-label", children: [stage.progressLabel, " (", progressPct, "%)"] }))] })), hasOutput && (_jsx("div", { className: "doc-status-stage-output", children: formatOutput(stage.output) })), showJson && hasOutput && (_jsx("pre", { className: "doc-status-stage-output-json", children: JSON.stringify(stage.output, null, 2) }))] }));
}
/** Format bytes to a human-readable label. Used for the upload
 *  stage's "12.4 MB / 98.6 MB" progress label. */
function formatBytes(n) {
    if (!n || n < 0)
        return '0 B';
    if (n < 1024)
        return `${n} B`;
    if (n < 1024 * 1024)
        return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024)
        return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
function overallGlyph(overall) {
    switch (overall) {
        case 'complete': return '\u2713';
        case 'failed': return '\u2717';
        case 'streaming':
        case 'running': return '\u25B6';
        case 'queued': return '\u22EF';
        default: return '\u00B7';
    }
}
function stageGlyph(status) {
    switch (status) {
        case 'complete': return '\u2713';
        case 'streaming': return '\u25B6';
        case 'failed': return '\u2717';
        case 'queued': return '\u22EF';
        default: return '\u00B7';
    }
}
function formatOutput(output) {
    if (!output || typeof output !== 'object')
        return '';
    const o = output;
    const parts = [];
    if (typeof o.pagesExtracted === 'number')
        parts.push(`${o.pagesExtracted} pages`);
    if (typeof o.totalChars === 'number')
        parts.push(`${o.totalChars.toLocaleString()} chars`);
    if (typeof o.chunkCount === 'number')
        parts.push(`${o.chunkCount} chunks`);
    if (typeof o.chunkMethod === 'string')
        parts.push(`(${o.chunkMethod})`);
    if (typeof o.embedTokens === 'number')
        parts.push(`${o.embedTokens.toLocaleString()} tokens`);
    if (typeof o.embedCost === 'number')
        parts.push(`$${o.embedCost.toFixed(6)}`);
    if (parts.length > 0)
        return parts.join(', ');
    return Object.keys(o).slice(0, 3).join(', ');
}
function durationStr(startedAt, completedAt) {
    if (!startedAt)
        return '';
    const start = new Date(startedAt).getTime();
    const end = completedAt ? new Date(completedAt).getTime() : Date.now();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start)
        return '';
    const ms = end - start;
    const sec = Math.round(ms / 1000);
    if (sec < 60)
        return `${sec}s`;
    const min = Math.floor(sec / 60);
    const remSec = sec % 60;
    return remSec > 0 ? `${min}m ${remSec}s` : `${min}m`;
}
